import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MdSend } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { postNowRequest, postNowReset } from '../../redux/slices/postNowSlice';
import PlatformSelector from './PlatformSelector';
import type { PostNowFormProps, SocialMedium } from './types';
import { useRestaurantNav } from '../../hooks/useRestaurantNav';

const PostNowForm: React.FC<PostNowFormProps> = ({
  flyerImageUrl,
  generatedContentId,
  flyerId,
  socialMediaData = [],
  restaurantId,
  onSubmit
}) => {
  const dispatch = useAppDispatch();
  const { navigate } = useRestaurantNav();
  const postNowState = useAppSelector((state) => state.postNow);
  const [selectedPlatforms, setSelectedPlatforms] = useState<SocialMedium[]>([]);

  const handlePlatformToggle = (platform: SocialMedium) => {
    setSelectedPlatforms(prev =>
      prev.includes(platform)
        ? prev.filter(p => p !== platform)
        : [...prev, platform]
    );
  };

  // Handle Post Now success
  useEffect(() => {
    if (postNowState.success) {
      const successCount = postNowState.results.filter(r => r.success).length;
      console.log(`Posted to ${successCount} platforms successfully!`);
      dispatch(postNowReset());
      navigate(`/${restaurantId}/marketing-calendar`);
      onSubmit(selectedPlatforms);
    }
  }, [postNowState.success, dispatch, navigate, restaurantId, onSubmit, selectedPlatforms]);

  // Handle errors
  useEffect(() => {
    if (postNowState.error) {
      console.error('Post now error:', postNowState.error);
    }
  }, [postNowState.error]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedPlatforms.length > 0) {
      console.log('Posting campaign now...');
      
      // Build platform-content mapping
      const platformContentMap: { [key: string]: number } = {};
      
      // Helper function to normalize platform names for matching
      const normalizePlatformName = (name: string): string => {
        const normalized = name.toUpperCase().replace(/[-_\s]/g, '');
        if (normalized === 'TWITTER' || normalized === 'TWITTERX' || normalized === 'X') {
          return 'TWITTER';
        }
        if (normalized === 'FACEBOOK' || normalized === 'FB') {
          return 'FACEBOOK';
        }
        if (normalized === 'INSTAGRAM' || normalized === 'IG') {
          return 'INSTAGRAM';
        }
        return normalized;
      };
      
      selectedPlatforms.forEach((platform) => {
        const normalizedPlatform = normalizePlatformName(platform);
        const content = socialMediaData.find(
          (item) => normalizePlatformName(item.primary_channel) === normalizedPlatform
        );
        
        if (content) {
          platformContentMap[platform.toLowerCase()] = content.content_id;
        } else {
          console.warn(`No content found for platform: ${platform}`);
        }
      });

      // Dispatch the Redux action
      dispatch(postNowRequest({
        platformContentMap,
        restaurantId,
        flyerId,
      }));
    }
  };

  const isFormValid = selectedPlatforms.length > 0;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Side: Flyer Preview */}
        <div className="lg:col-span-1">
          <h3 className="text-sm font-semibold text-gray-400 mb-3">Flyer Preview</h3>
          <div className="relative rounded-xl overflow-hidden shadow-lg border border-gray-700">
            <img
              src={flyerImageUrl}
              alt="Flyer Preview"
              className="w-full h-auto object-cover"
            />
          </div>
        </div>

        {/* Right Side: Platform Selection */}
        <div className="lg:col-span-2 space-y-6">
          <PlatformSelector
            selectedPlatforms={selectedPlatforms}
            onToggle={handlePlatformToggle}
          />

          {/* Submit Button */}
          <div className="flex justify-end pt-4">
            <motion.button
              type="submit"
              whileHover={{ scale: (isFormValid && !postNowState.loading) ? 1.02 : 1 }}
              whileTap={{ scale: (isFormValid && !postNowState.loading) ? 0.98 : 1 }}
              disabled={!isFormValid || postNowState.loading}
              className={`px-6 py-2.5 text-sm font-semibold rounded-full flex items-center justify-center gap-2 transition-colors ${
                (isFormValid && !postNowState.loading)
                  ? 'bg-[#EE4423] hover:bg-[#d63d20] text-white cursor-pointer'
                  : 'bg-gray-700 text-gray-500 cursor-not-allowed'
              }`}
            >
              {postNowState.loading ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Posting...</span>
                </>
              ) : (
                <>
                  <MdSend size={18} />
                  Post Now
                </>
              )}
            </motion.button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default PostNowForm;
